import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { AndieMemoryProvider } from './andie-memory-provider';
import { AndieChatProvider } from './andie-chat-provider';
import { Post } from '@/components/post-planning/providers/PostsProvider';

type HelperFunctions = {
    addPost: (post: Post) => void;
    updatePost: (post: Post) => void;
    removePost: (postId: string) => void;
    navigateToPost: (postId: string, ignorePostOpenCheck?: boolean) => void;
    navigateBackFromPost: () => void;
};

const Context = createContext<{
    open: boolean;
    setOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
    processingFirstMessage: React.MutableRefObject<boolean>;
    inputValueRef: React.MutableRefObject<string>;
    helpers: HelperFunctions;
    setHelper: (fName: keyof HelperFunctions, f: HelperFunctions[keyof HelperFunctions]) => void;
} | null>(null);

export function AndieProvider({ children }: { children: React.ReactNode }) {
    const [open, setOpenState] = useState<boolean>(() =>
        typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('andie-open') === '1' : false,
    );

    const setOpen = (v: boolean | ((prev: boolean) => boolean)) => {
        setOpenState((prev) => {
            const next = typeof v === 'function' ? v(prev) : v;
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem('andie-open', next ? '1' : '0');
            }
            return next;
        });
    };

    const [helpers, setHelpers] = useState<HelperFunctions>({
        addPost: () => {
            console.warn('addPost helper not implemented');
        },
        updatePost: () => {
            console.warn('updatePost helper not implemented');
        },
        removePost: () => {
            console.warn('removePost helper not implemented');
        },
        navigateToPost: () => {
            console.warn('navigateToPost helper not implemented');
        },
        navigateBackFromPost: () => {
            console.warn('navigateBackFromPost helper not implemented');
        },
    });

    const setHelper = useCallback(
        (fName: keyof HelperFunctions, f: HelperFunctions[keyof HelperFunctions]) => {
            setHelpers((prev) => ({
                ...prev,
                [fName]: f,
            }));
        },
        [],
    );

    const processingFirstMessage = useRef(
        typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('andie-open') === '1' : false,
    );

    const inputValueRef = useRef('');

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.key === 'k') {
                event.preventDefault();
                setOpen((prev) => !prev);
            } else if (event.key === 'Escape') {
                setOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <Context.Provider
            value={{
                open,
                setOpen,
                processingFirstMessage,
                inputValueRef,
                helpers,
                setHelper,
            }}
        >
            <AndieChatProvider>
                <AndieMemoryProvider>{children}</AndieMemoryProvider>
            </AndieChatProvider>
        </Context.Provider>
    );
}

export function useAndie() {
    const currentContext = useContext(Context);
    if (!currentContext) {
        throw new Error('useAndie must be used within an AndieProvider');
    }
    return currentContext;
}
